import * as React from "react";
import { format } from "lib/globalize-util";
import Checkbox from "material-ui/Checkbox";
import { List, ListItem } from "material-ui/List";
import Toggle from "material-ui/Toggle";
import Label from "components/form/label";
import { Set } from "immutable";
import DatePicker from "components/form/date-picker.js";
import TextField from "components/form/text-field.js";
import Dropdown from "components/form/dropdown";
import moment from "moment";
import Separator from "components/form/separator.js";
import { rawTheme } from "styles/theme";
import { Form } from "formsy-react";
import CurrencyMultiSelect from "./currency-multi-select";

interface IProps {
    warehouses: any;
    selectedWarehouses: Set<any>;
    onWarehouseChange: (event, warehouses: Set<any>) => void;
    availFromDate: moment.Moment;
    availUntilDate?: moment.Moment;
    onDateChange: (event, date: moment.Moment) => void;
    minQuantity: number;
    onQuantityChange: (event, qty: number | string) => void;
    atsOnly: boolean;
    onATSChange: (event, value: boolean) => void;
    excludeBrokenRange?: boolean;
    onExcludeChange?: (event, value: boolean) => void;
    availableCurrencies?: string[] | null;
    availableMsrpCurrencies?: string[] | null;
    selectedWholesaleCurrencies?: Set<string>;
    selectedRetailCurrencies?: Set<string>;
    currencyCode?: string | null;
    onWholesaleCurrencyToggle?: (selected: Set<string>) => void;
    onRetailCurrencyToggle?: (selected: Set<string>) => void;
    isWholesaleCurrencyVisible?: boolean;
    isRetailCurrencyVisible?: boolean;
    router?: any;
}

/**
 * Stateless component for displaying and managing Order From filters.
 * Should be put to form components.
 */
export default class ATSForm extends React.PureComponent<IProps> {
    private styles = {
        input: {
            display: "block",
            marginBottom: "8px",
        },
        clearDate: {
            top: "18px",
        },
        clearText: {
            top: "71%",
        },
        marginSmall: {
            marginTop: "10px",
        },
        marginLarge: {
            marginTop: "20px",
        },
        toggleLabel: {
            fontSize: "12px",
            color: rawTheme.palette.darkGrey,
        },
        deliveryWindow: {
            margin: "6px 0",
        },
    };

    render() {
        const {
            atsOnly,
            onATSChange,
            availFromDate,
            availUntilDate,
            minQuantity,
            warehouses,
            selectedWarehouses,
            onQuantityChange,
            excludeBrokenRange,
            onExcludeChange,
            availableCurrencies,
            availableMsrpCurrencies,
            selectedWholesaleCurrencies,
            selectedRetailCurrencies,
            currencyCode,
            onWholesaleCurrencyToggle,
            onRetailCurrencyToggle,
            isWholesaleCurrencyVisible,
            isRetailCurrencyVisible,
        } = this.props;
        let warehouseDisplay;

        if (warehouses && warehouses.size > 1) {
            const label = selectedWarehouses.map((id) => warehouses.get(id)).join(", ");
            const items = Array.from(warehouses.entries(), ([id, name]) => {
                const included = selectedWarehouses.includes(id);
                return (
                    <ListItem
                        key={id}
                        primaryText={name}
                        leftCheckbox={<Checkbox onCheck={this.handleWarehouseToggle(id)} checked={included} />}
                    />
                );
            });
            warehouseDisplay = (
                <div>
                    <Label>{format("Warehouses")}</Label>
                    <Dropdown label={label}>
                        <List>{items}</List>
                    </Dropdown>
                </div>
            );
        }

        let dateDisplay = (
            <DatePicker
                name="availableFrom"
                style={this.styles.input}
                onChange={this.dateChange}
                label={format("Only include products available on")}
                value={availFromDate}
                clearable={true}
                clearStyle={this.styles.clearDate}
            />
        );

        if (availFromDate && availUntilDate) {
            dateDisplay = (
                <div style={this.styles.deliveryWindow}>
                    <Label>{format("Delivery Dates")}</Label>
                    <div className="delivery-dates">
                        {availFromDate.format("MMM DD, YYYY")} - {availUntilDate.format("MMM DD, YYYY")}
                    </div>
                </div>
            );
        }

        return (
            <div>
                <Form>
                    {warehouseDisplay}
                    <Separator />
                    <h4>{format("Order Form Filters")}</h4>
                    {dateDisplay}
                    <TextField
                        fullWidth={true}
                        label={format(
                            "Only include products with at least # units (applied only with the date, can be left blank)"
                        )}
                        canClear={true}
                        clearStyle={this.styles.clearText}
                        onChange={onQuantityChange}
                        name="minQuantity"
                        value={minQuantity}
                        type="number"
                        style={this.styles.input}
                        disabled={!availFromDate}
                    />
                    <Toggle
                        label={format("Include ATS products only")}
                        toggled={atsOnly}
                        onToggle={onATSChange}
                        style={this.styles.marginLarge}
                        labelStyle={this.styles.toggleLabel}
                    />
                    <Toggle
                        label={format("Exclude products not available in all sizes")}
                        onToggle={onExcludeChange}
                        toggled={excludeBrokenRange}
                        style={this.styles.marginSmall}
                        labelStyle={this.styles.toggleLabel}
                    />
                    {isWholesaleCurrencyVisible && availableCurrencies?.length > 1 && (
                        <>
                            <Separator />
                            {isWholesaleCurrencyVisible && (
                                <CurrencyMultiSelect
                                    currencies={availableCurrencies}
                                    selected={selectedWholesaleCurrencies}
                                    defaultCurrency={currencyCode}
                                    label={format("Wholesale currency")}
                                    onToggle={onWholesaleCurrencyToggle}
                                />
                            )}
                        </>
                    )}
                    {isRetailCurrencyVisible && availableMsrpCurrencies?.length > 1 && (
                        <>
                            {isRetailCurrencyVisible && (
                                <CurrencyMultiSelect
                                    currencies={availableMsrpCurrencies}
                                    selected={selectedRetailCurrencies}
                                    label={format("Retail currency")}
                                    onToggle={onRetailCurrencyToggle}
                                />
                            )}
                        </>
                    )}
                </Form>
            </div>
        );
    }

    // remove minQuantity if there's no date
    private dateChange = (event, value) => {
        this.props.onDateChange(event, value);
        if (!value) {
            this.props.onQuantityChange(event, "");
        }
    };

    private handleWarehouseToggle = (id: string) => () => {
        const { selectedWarehouses, onWarehouseChange } = this.props;
        if (!selectedWarehouses.includes(id)) {
            onWarehouseChange(null, selectedWarehouses.add(id));
        } else if (selectedWarehouses.size > 1) {
            onWarehouseChange(null, selectedWarehouses.delete(id));
        }
    };
}
